import { useEffect, useState } from "react";
import { Coordinate } from "ol/coordinate";
import { Extent } from "ol/extent";
import { ObjectEvent } from "ol/Object";
import { useMapContext } from "@avinet/adaptive-ui-maps";

interface MapListenerProps {
  onCenterChange?: (center: Coordinate) => void;
  onZoomChange?: (zoom: number) => void;
  onExtentChange?: (extent: Extent) => void;
  onStateChange?: (center: Coordinate, mapZoom: number) => void;
}

const MapListener = ({
  onCenterChange,
  onZoomChange,
  onExtentChange,
  onStateChange,
}: MapListenerProps) => {
  const { view } = useMapContext();
  // Prevent extent updates until map is likely loaded
  const [isActive, setActive] = useState(false);

  const [mapCenter, setCenter] = useState([0, 0]);
  const [mapZoom, setZoom] = useState(0);
  const [mapExtent, setExtent] = useState([0, 0, 0, 0]);

  useEffect(() => {
    const center = view.getCenter();
    const zoom = view.getZoom();
    if (center && zoom !== undefined) {
      setCenter(center);
      setZoom(zoom);
      setExtent(view.calculateExtent());
    }

    function handle(event: ObjectEvent) {
      switch (event.key) {
        case "center": {
          const center = view.getCenter();
          const zoom = view.getZoom();
          if (center) setCenter(center);
          if (center && zoom !== undefined) {
            setExtent(view.calculateExtent());
          }
          break;
        }

        case "resolution": {
          const center = view.getCenter();
          const zoom = view.getZoom();
          if (zoom !== undefined) setZoom(zoom);
          if (center && zoom !== undefined) {
            setExtent(view.calculateExtent());
          }
          break;
        }
      }
    }

    view.on("propertychange", handle);
    return () => view.un("propertychange", handle);
  }, [view]);

  useEffect(() => {
    const tId = setTimeout(() => setActive(true), 2000);
    return () => clearTimeout(tId);
  }, []);

  useEffect(
    () => onStateChange && onStateChange(mapCenter, mapZoom),
    [mapCenter, mapZoom, onStateChange]
  );
  useEffect(
    () => onCenterChange && onCenterChange(mapCenter),
    [mapCenter, onCenterChange]
  );
  useEffect(
    () => onZoomChange && onZoomChange(mapZoom),
    [mapZoom, onZoomChange]
  );

  useEffect(() => {
    if (!isActive) return;
    onExtentChange && onExtentChange(mapExtent);
  }, [isActive, mapExtent, onExtentChange]);
  return null;
};

export default MapListener;
